/**
 * Reading stored dates for display.
 *
 * A date-only value ("2026-10-21" — a target close, a condition's due date,
 * the day a package was received) names a calendar day, not an instant.
 * `new Date("2026-10-21")` reads it as midnight UTC, and every US time zone
 * is behind UTC, so `toLocaleDateString` printed it as 10/20/2026: every
 * dossier date was one day early for the desk while the server-built exports
 * kept the stored day. A date-only value is read here as that day in the
 * reader's own zone; anything carrying a time keeps the instant it states.
 */

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const MONTH_ONLY_RE = /^(\d{4})-(\d{2})$/

/** A stored date or timestamp as a Date, or null when it names no real date. */
export function parseDateValue(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  if (!text) return null
  const match = DATE_ONLY_RE.exec(text)
  if (match) {
    const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])]
    const local = new Date(year, month, day)
    // `new Date(2026, 1, 30)` rolls over to 2 March; a day that does not exist
    // is not a date, exactly as the ISO parser treats "2026-02-30".
    const exists =
      local.getFullYear() === year && local.getMonth() === month && local.getDate() === day
    return exists ? local : null
  }
  // A month a rent roll states ("Jan-27" is stored "2027-01") is that month
  // here too. `new Date("2027-01")` is midnight UTC on 1 January — the evening
  // of 31 December 2026 in New York — so a January expiry charted a year early.
  const month = MONTH_ONLY_RE.exec(text)
  if (month) {
    const index = Number(month[2]) - 1
    return index >= 0 && index < 12 ? new Date(Number(month[1]), index, 1) : null
  }
  const parsed = new Date(text)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * A stored date as the value a date input holds ("YYYY-MM-DD"), or null when
 * it names no real date.
 *
 * The edit box and the read-only dossier must show the same day, so both go
 * through `parseDateValue`: a date-only value is that calendar day, and an
 * instant is the day it fell on in the reader's zone. The box used to receive
 * the stored text as-is, so a received date read 8/18/2026 locked and
 * 08/19/2026 unlocked.
 */
export function dateInputValue(value: unknown): string | null {
  const date = parseDateValue(value)
  if (!date) return null
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** The desk's time zone. Timestamps are stored as instants; the desk reads them in New York. */
export const DESK_TIME_ZONE = 'America/New_York'

const ZONED_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i
const DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/

/**
 * The instant a stored timestamp names, or null when the text is not one.
 *
 * The API sends most timestamps naive (SQLite drops the zone) and every naive
 * timestamp on the platform is UTC; `new Date()` would read one as the browser's
 * local time, which is a different answer on every desk member's machine.
 */
function deskInstant(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  if (!DATE_TIME_RE.test(text)) return null
  const instant = new Date(ZONED_RE.test(text) ? text : `${text.replace(' ', 'T')}Z`)
  return Number.isNaN(instant.getTime()) ? null : instant
}

/** Text of a value that names no date at all, returned untouched ('' when empty). */
function asWritten(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim()
}

export type DeskDateStyle = 'numeric' | 'short'

const DESK_DATE_OPTIONS: Record<DeskDateStyle, Intl.DateTimeFormatOptions> = {
  numeric: { year: 'numeric', month: 'numeric', day: 'numeric' },
  short: { month: 'short', day: '2-digit' },
}

/**
 * The calendar day a stored value falls on, as the New York desk reads it:
 * "8/18/2026" (`numeric`) or "Aug 18" (`short`).
 *
 * THE one date formatter for anything that came from the server. A browser's
 * `toLocaleDateString(undefined)` prints an instant in the reader's own zone, so
 * the same message read 8/18 in New York and 8/19 for a colleague in Tokyo, and
 * a column rendered with it disagreed with the server-built exports (which use
 * the desk day — `core.desk_calendar.desk_date`).
 *
 * A bare "YYYY-MM-DD" is a calendar day, not an instant, and prints as that day
 * in every zone: it is never shifted. Text that names no date comes back as
 * written, so "date not recorded" survives; null and '' give ''.
 */
export function formatDeskDate(value: unknown, style: DeskDateStyle = 'numeric'): string {
  const written = asWritten(value)
  if (!written) return ''
  if (DATE_ONLY_RE.test(written)) {
    const day = parseDateValue(written)
    return day ? day.toLocaleDateString('en-US', DESK_DATE_OPTIONS[style]) : written
  }
  const instant = deskInstant(value)
  if (!instant) return written
  return instant.toLocaleDateString('en-US', { timeZone: DESK_TIME_ZONE, ...DESK_DATE_OPTIONS[style] })
}

/**
 * The desk-clock time of day of a stored instant: "9:30 PM". '' for a bare day
 * (it has no time) and for text that names no instant.
 */
export function formatDeskTime(value: unknown): string {
  const instant = deskInstant(value)
  if (!instant) return ''
  return instant.toLocaleTimeString('en-US', {
    timeZone: DESK_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * A stored timestamp as the desk reads it: "9/26/2026, 8:01 AM ET".
 *
 * The kill panel printed 'dev@local · 2026-09-26T12:01:06.307995' and the
 * intake package 'Sent 2026-08-03T10:20:00-04:00' (nd-032, nd-072). A bare day
 * prints as that day with no time; text that names no date is returned as
 * written.
 */
export function formatDeskDateTime(value: unknown): string {
  const written = asWritten(value)
  if (!written) return ''
  const instant = deskInstant(value)
  if (!instant) return formatDeskDate(value)
  const stamp = instant.toLocaleString('en-US', {
    timeZone: DESK_TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  return `${stamp} ET`
}

/**
 * A stored timestamp as a sortable stamp on the desk's clock: "2026-08-18 21:30".
 *
 * The shape table cells use where "9/26/2026, 8:01 AM ET" is too wide. A bare
 * "YYYY-MM-DD" is a calendar day and is returned as that day, unshifted; text
 * that names no date comes back as written, and null/'' give ''.
 */
export function formatDeskStamp(value: unknown): string {
  const written = asWritten(value)
  if (!written) return ''
  if (DATE_ONLY_RE.test(written)) return written                    // a day: never shifted
  const instant = deskInstant(value)
  if (!instant) return written
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: DESK_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant)
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}`
}

/**
 * The desk-calendar day of a stored value as "YYYY-MM-DD", or null when it names
 * no real date. The ISO twin of `core.desk_calendar.desk_date_iso`: an instant
 * is the New York day it fell on, a bare day is itself. (`dateInputValue` is for
 * a form's date input and reads an instant in the BROWSER's zone.)
 */
export function deskDayValue(value: unknown): string | null {
  const written = asWritten(value)
  if (!written) return null
  if (DATE_ONLY_RE.test(written)) return parseDateValue(written) ? written : null
  const stamp = formatDeskStamp(value)
  return /^\d{4}-\d{2}-\d{2} /.test(stamp) ? stamp.slice(0, 10) : null
}

/** Alias kept for existing callers: the same reading as `formatDeskDateTime`. */
export function formatTimestamp(value: unknown): string {
  return formatDeskDateTime(value)
}
